import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faDownload, 
    faSearch, 
    faFilter, 
    faChevronLeft, 
    faChevronRight,
    faClipboardList,
    faUser,
    faClock,
    faCalendarAlt,
    faTimes,
    faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';

const PaymentAudit = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [pagination, setPagination] = useState({});
    
    // Filter states
    const [filters, setFilters] = useState({
        action: '',
        actor_role: '',
        dateFrom: '',
        dateTo: '',
        search: ''
    });
    
    // UI states
    const [showFilters, setShowFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage] = useState(10);
    const [searchInput, setSearchInput] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    useEffect(() => {
        fetchLogs();
        setSearchInput(filters.search);
    }, []);

    useEffect(() => {
        fetchLogs();
    }, [currentPage, filters]);

    // Debounced search effect
    useEffect(() => {
        if (searchInput !== filters.search) {
            setIsSearching(true);
            const timeoutId = setTimeout(() => {
                setFilters(prev => ({ ...prev, search: searchInput }));
                setCurrentPage(1);
                setIsSearching(false);
            }, 500);
            return () => clearTimeout(timeoutId);
        }
    }, [searchInput, filters.search]);

    const fetchLogs = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            
            const params = new URLSearchParams({
                page: currentPage,
                per_page: perPage,
                ...Object.fromEntries(
                    Object.entries(filters).filter(([_, value]) => value !== '')
                )
            });

            const response = await api.get(`/audit/payments?${params}`);
            
            if (response.data.success) {
                setLogs(response.data.data.data || []);
                setPagination(response.data.data.pagination || {});
            } else {
                setError('Erreur lors du chargement des logs d\'audit');
            }
        } catch (err) {
            console.error('Error fetching payment audit logs:', err);
            if (err.response?.status === 403) {
                setError('Accès non autorisé');
            } else if (err.response?.status === 401) {
                setError('Session expirée, veuillez vous reconnecter');
            } else {
                setError('Erreur lors du chargement des logs d\'audit des paiements');
            }
        } finally {
            setLoading(false);
        }
    }, [currentPage, perPage, filters]);

    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({ ...prev, [filterName]: value }));
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setFilters({
            action: '',
            actor_role: '',
            dateFrom: '',
            dateTo: '',
            search: ''
        });
        setSearchInput('');
        setCurrentPage(1);
    };

    const hasActiveFilters = Object.values(filters).some(value => value !== '');

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.last_page) {
            setCurrentPage(newPage);
        }
    };

    const renderPagination = () => {
        const totalPages = pagination.last_page || 1;
        const totalCount = pagination.total || 0;

        if (totalPages <= 1) return null;

        return (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4">
                <div className="flex items-center text-xs sm:text-sm text-gray-700 text-center sm:text-left whitespace-nowrap">
                    <span>
                        Affichage de {((currentPage - 1) * perPage) + 1} à {Math.min(currentPage * perPage, totalCount)} sur {totalCount} logs d'audit
                    </span>
                </div>
                
                <div className="flex items-center justify-center gap-1 sm:gap-2 flex-wrap">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-2 sm:px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                    >
                        <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                    </button>
                    
                    <div className="flex space-x-1 flex-wrap justify-center">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                                pageNum = i + 1;
                            } else if (currentPage <= 3) {
                                pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                            } else {
                                pageNum = currentPage - 2 + i;
                            }

                            return (
                                <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    className={`px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors flex-shrink-0 ${
                                        currentPage === pageNum
                                            ? 'bg-red-600 text-white shadow-md'
                                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}
                    </div>
                    
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-2 sm:px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                    >
                        <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                    </button>
                </div>
            </div>
        );
    };

    const getActionColor = (action) => {
        if (action.includes('create') || action.includes('Création')) {
            return 'bg-green-100 text-green-800';
        } else if (action.includes('update') || action.includes('Modification')) {
            return 'bg-blue-100 text-blue-800';
        } else if (action.includes('delete') || action.includes('Suppression') || action.includes('archive')) {
            return 'bg-red-100 text-red-800';
        } else if (action.includes('verify') || action.includes('Vérification')) {
            return 'bg-purple-100 text-purple-800';
        } else if (action.includes('lock') || action.includes('Verrouillage')) {
            return 'bg-orange-100 text-orange-800';
        } else if (action.includes('confirm') || action.includes('Confirmation')) {
            return 'bg-emerald-100 text-emerald-800';
        } else if (action.includes('export') || action.includes('Export')) {
            return 'bg-indigo-100 text-indigo-800';
        } else if (action.includes('reject') || action.includes('Rejet')) {
            return 'bg-red-100 text-red-800';
        } else {
            return 'bg-gray-100 text-gray-800';
        }
    };

    const formatChanges = (changes) => {
        if (!changes || typeof changes !== 'object') return 'N/A';
        
        // Define raw database fields that should not be displayed
        const rawDatabaseFields = ['is_active', 'created_at', 'updated_at', 'deleted_at'];
        
        // Filter out raw database fields and get valid entries
        const validEntries = Object.entries(changes).filter(([key]) => !rawDatabaseFields.includes(key));
        
        // If no valid entries after filtering, return a dash or empty
        if (validEntries.length === 0) {
            return '-';
        }
        
        return validEntries.map(([key, value]) => {
            // Handle different value formats
            let oldValue, newValue;
            
            if (Array.isArray(value) && value.length === 2) {
                // Standard format: [oldValue, newValue]
                [oldValue, newValue] = value;
            } else if (typeof value === 'string' || typeof value === 'number') {
                // Single value format (like rejection_note or modification_reason)
                newValue = value;
                oldValue = null;
            } else {
                // Fallback
                oldValue = value;
                newValue = null;
            }
            
            // Special handling for rejection notes to make them more prominent
            if (key === 'rejection_note') {
                return (
                    <div key={key} className="text-xs mb-2 p-2 bg-red-50 border border-red-200 rounded">
                        <span className="font-medium text-red-800 block mb-1">Raison de rejet:</span>
                        <span className="text-red-700 italic">{newValue || oldValue}</span>
                    </div>
                );
            }
            
            // Special handling for modification reasons
            if (key === 'modification_reason') {
                return (
                    <div key={key} className="text-xs mb-2 p-2 bg-blue-50 border border-blue-200 rounded">
                        <span className="font-medium text-blue-800 block mb-1">Raison de modification:</span>
                        <span className="text-blue-700 italic">{newValue || oldValue}</span>
                    </div>
                );
            }

            // Special handling for delegation fields
            if (key === 'delegate_1_name' || key === 'delegate_2_name' || key === 'delegate_1_id' || key === 'delegate_2_id') {
                const delegateNumber = (key === 'delegate_1_name' || key === 'delegate_1_id') ? '1' : '2';
                const isIdField = key.includes('_id');
                
                return (
                    <div key={key} className="text-xs mb-1">
                        <span className="font-medium text-gray-700">Délégué {delegateNumber}:</span>
                        {oldValue !== null && oldValue !== undefined ? (
                            <>
                                <span className="ml-1 text-red-600">{oldValue || 'Aucun'}</span>
                                <span className="mx-1">→</span>
                                <span className="text-green-600">{newValue !== null && newValue !== undefined ? newValue : 'N/A'}</span>
                            </>
                        ) : (
                            <span className="ml-1 text-green-600">
                                {isIdField && newValue 
                                    ? `User ID: ${newValue}` 
                                    : (newValue !== null && newValue !== undefined ? newValue : 'N/A')
                                }
                            </span>
                        )}
                    </div>
                );
            }
            
            // Regular change display
            return (
                <div key={key} className="text-xs text-gray-600 mb-1">
                    <span className="font-medium">{key}:</span>
                    {oldValue !== null && oldValue !== undefined ? (
                        <>
                            <span className="ml-1 text-red-600">{oldValue}</span>
                            <span className="mx-1">→</span>
                            <span className="text-green-600">{newValue !== null && newValue !== undefined ? newValue : 'N/A'}</span>
                        </>
                    ) : (
                        <span className="ml-1 text-green-600">{newValue !== null && newValue !== undefined ? newValue : 'N/A'}</span>
                    )}
                </div>
            );
        });
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
                    <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center mb-2">
                                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 truncate">
                                    AUDIT DES PAIEMENTS
                                </h1>
                            </div>
                            <p className="text-gray-600 text-sm sm:text-base">Suivi des actions et modifications sur les paiements</p>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6 border border-gray-200">
                    <div className="mb-4">
                        {/* Top Row - Search and Filter Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4 mb-4">
                            {/* Search */}
                            <div className="relative flex-1">
                                <FontAwesomeIcon
                                    icon={isSearching ? faExclamationTriangle : faSearch}
                                    className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                                        isSearching ? 'text-orange-400 animate-pulse' : 'text-gray-400'
                                    }`}
                                />
                                <input
                                    type="text"
                                    placeholder="Rechercher dans les logs..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                />
                            </div>

                            {/* Filter Buttons */}
                            <div className="flex items-center space-x-2 flex-shrink-0">
                                <button
                                    onClick={() => setShowFilters(!showFilters)}
                                    className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center text-sm sm:text-base ${
                                        showFilters || hasActiveFilters
                                            ? 'bg-red-600 text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    <FontAwesomeIcon icon={faFilter} className="mr-1 sm:mr-2" />
                                    <span>Filtres</span>
                                </button>
                                {hasActiveFilters && (
                                    <button
                                        onClick={clearFilters}
                                        className="px-3 sm:px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors duration-200 flex items-center text-sm sm:text-base"
                                    >
                                        <FontAwesomeIcon icon={faTimes} className="mr-1 sm:mr-2" />
                                        <span>Effacer</span>
                                    </button>
                                )}
                            </div>
                        </div>

                    </div>

                    {/* Extended Filters */}
                    {showFilters && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
                            {/* Action Filter */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Action
                                </label>
                                <select
                                    value={filters.action}
                                    onChange={(e) => handleFilterChange('action', e.target.value)}
                                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm sm:text-base"
                                >
                                    <option value="">Toutes les actions</option>
                                    <option value="create">Création</option>
                                    <option value="verify">Vérification</option>
                                    <option value="lock">Verrouillage</option>
                                    <option value="export">Export</option>
                                    <option value="confirm">Confirmation</option>
                                    <option value="delegate_verification">Délégation</option>
                                    <option value="reject">Rejet</option>
                                    <option value="modify_payment_status">Modification statut</option>
                                </select>
                            </div>

                            {/* Actor Role Filter */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Rôle Acteur
                                </label>
                                <select
                                    value={filters.actor_role}
                                    onChange={(e) => handleFilterChange('actor_role', e.target.value)}
                                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm sm:text-base"
                                >
                                    <option value="">Tous les rôles</option>
                                    <option value="administrateur_general_superieur">Business Officer</option>
                                    <option value="administrateur_general">Administrateur Général</option>
                                    <option value="resp_admin">Resp Admin</option>
                                </select>
                            </div>

                            {/* Date From */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-gray-400" />
                                    Date de début
                                </label>
                                <input
                                    type="date"
                                    value={filters.dateFrom}
                                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm sm:text-base"
                                />
                            </div>

                            {/* Date To */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-gray-400" />
                                    Date de fin
                                </label>
                                <input
                                    type="date"
                                    value={filters.dateTo}
                                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm sm:text-base"
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm sm:text-base">
                        {error}
                    </div>
                )}

                {/* Loading State */}
                {loading ? (
                    <div className="bg-white rounded-lg shadow-sm p-8 sm:p-12 text-center border border-gray-200">
                        <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
                        <p className="text-gray-600 text-sm sm:text-base">Chargement des logs d'audit...</p>
                    </div>
                ) : logs.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-sm p-8 sm:p-12 text-center border border-gray-200">
                        <FontAwesomeIcon icon={faClipboardList} className="text-4xl sm:text-6xl text-gray-300 mb-4" />
                        <p className="text-gray-600 text-base sm:text-lg">Aucun log d'audit trouvé</p>
                        <p className="text-gray-500 text-xs sm:text-sm mt-2">Aucune action sur les paiements n'a encore été enregistrée</p>
                    </div>
                ) : (
                    <>
                        {/* Desktop Table */}
                        <div className="hidden lg:block bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            <FontAwesomeIcon icon={faClock} className="mr-2" />
                                            Date/Heure
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            <FontAwesomeIcon icon={faUser} className="mr-2" />
                                            Acteur
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Action
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Lot
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Description
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Modifications
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {logs.map((log) => (
                                        <tr key={log.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm text-gray-900">{log.when}</span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">{log.actor}</div>
                                                    <div className="text-sm text-gray-500">{log.actor_role}</div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActionColor(log.action)}`}>
                                                    {log.action}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-sm text-gray-900">{log.target}</span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900">
                                                    {log.description || 'N/A'}
                                                    {/* Show rejection note from meta if it exists and not in changes */}
                                                    {log.meta && log.meta.rejection_note && !(log.changes && log.changes.rejection_note) && (
                                                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs">
                                                            <span className="font-medium text-red-800">Raison de rejet:</span>
                                                            <div className="text-red-700 italic mt-1">{log.meta.rejection_note}</div>
                                                        </div>
                                                    )}
                                                    {/* Show modification reason from meta if it exists and not in changes */}
                                                    {log.meta && log.meta.modification_reason && !(log.changes && log.changes.modification_reason) && (
                                                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                                                            <span className="font-medium text-blue-800">Raison de modification:</span>
                                                            <div className="text-blue-700 italic mt-1">{log.meta.modification_reason}</div>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="max-w-xs">
                                                    {formatChanges(log.changes)}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Cards */}
                        <div className="lg:hidden space-y-4">
                            {logs.map((log) => (
                                <div key={log.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                                    {/* Header Row */}
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-gray-900 truncate">{log.actor}</div>
                                            <div className="text-xs text-gray-500">{log.actor_role}</div>
                                        </div>
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full flex-shrink-0 ml-2 ${getActionColor(log.action)}`}>
                                            {log.action}
                                        </span>
                                    </div>

                                    {/* Meta Info */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                        <div>
                                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Date/Heure</div>
                                            <div className="text-sm text-gray-900 break-words">{log.when}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Lot</div>
                                            <div className="text-sm text-gray-900">{log.target}</div>
                                        </div>
                                    </div>

                                    {/* Description */}
                                    <div className="mb-4">
                                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Description</div>
                                        <div className="text-sm text-gray-900 break-words">
                                            {log.description || 'N/A'}
                                            {/* Show rejection note from meta if it exists and not in changes */}
                                            {log.meta && log.meta.rejection_note && !(log.changes && log.changes.rejection_note) && (
                                                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs">
                                                    <span className="font-medium text-red-800 block">Raison de rejet:</span>
                                                    <div className="text-red-700 italic mt-1 break-words">{log.meta.rejection_note}</div>
                                                </div>
                                            )}
                                            {/* Show modification reason from meta if it exists and not in changes */}
                                            {log.meta && log.meta.modification_reason && !(log.changes && log.changes.modification_reason) && (
                                                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                                                    <span className="font-medium text-blue-800 block">Raison de modification:</span>
                                                    <div className="text-blue-700 italic mt-1 break-words">{log.meta.modification_reason}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Changes */}
                                    <div>
                                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Modifications</div>
                                        <div className="text-sm">
                                            {formatChanges(log.changes)}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        {renderPagination()}
                    </>
                )}
            </div>
        </div>
    );
};

export default PaymentAudit;
